import countriesData from "./countriesData.json";
import EnglishData from "./EnglishData.json";
import SpanishData from "./SpanishData.json";
import {
  CONTINENT_LABELS,
  CONTINENTS,
  Continent,
  parsedCountryName,
  resolveContinent,
} from "./functions";
import { AvailableLanguages, Country } from "./types";

export type AutotestQuestionType =
  // recognition
  | "flag"
  | "flagReverse"
  | "continent"
  | "capital"
  // recall
  | "currency"
  | "language"
  | "sea"
  | "border"
  | "mapPoint"
  // reasoning
  | "notBorder"
  | "oddOneOut"
  | "combo"
  | "biggestPopulation";

export type AutotestDifficulty = "easy" | "medium" | "hard";

export interface AutotestOption {
  label: string;
  /** Set on flag-picking questions, where the options are images. */
  flagUrl?: string;
}

export interface AutotestQuestion {
  type: AutotestQuestionType;
  /** Subject country — used to keep a run from repeating the same country. */
  countryName: string;
  prompt: string;
  /** Rendered above the prompt (flag → country questions). */
  flagUrl?: string;
  /** English name of the country to tap, for "mapPoint" questions. */
  mapTargetName?: string;
  /** The map is cropped to this continent so the target is a fair tap size. */
  mapTargetContinent?: Continent;
  options: AutotestOption[];
  correctIndex: number;
  /** Shown after answering, so a wrong answer still teaches something. */
  explanation: string;
  /** Country page to offer as "read more" after answering. */
  learnMoreCountry?: string;
}

// ---------------------------------------------------------------------------
// Randomness
// ---------------------------------------------------------------------------

export type Rng = () => number;

// Deterministic PRNG so the daily challenge can hand everyone the same run
// from the same date seed. Classic runs just pass Math.random.
export const seededRng = (seed: number): Rng => {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

export const dailySeed = (date = new Date()) =>
  date.getFullYear() * 10000 + (date.getMonth() + 1) * 100 + date.getDate();

const shuffle = <T,>(items: T[], rng: Rng): T[] => {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

const sample = <T,>(items: T[], count: number, rng: Rng): T[] =>
  shuffle(items, rng).slice(0, count);

const pickOne = <T,>(items: T[], rng: Rng): T | undefined =>
  items.length === 0 ? undefined : items[Math.floor(rng() * items.length)];

// ---------------------------------------------------------------------------
// Parsing the (free-text) dataset
// ---------------------------------------------------------------------------

// Currency strings carry explanatory sentences past the ISO code, e.g.
// "Balboa (PAB), though the US Dollar (USD) is legal tender." — trim down to
// the clean "name (CODE)" part so it reads well as a multiple-choice option.
const cleanCurrencyLabel = (raw: string) =>
  raw.match(/^(.*?\([A-Z]{3}\))/)?.[1]?.trim() ?? raw.trim();

// Same idea for capitals: several entries list more than one city with a
// parenthetical qualifier ("Pretoria (administrative), Cape Town
// (legislative)."). Keep just the first one as the quiz's "correct" answer.
// Trailing punctuation is common in the source ("Abu Dhabi.") and looks like
// a typo next to the other options.
const cleanCapitalLabel = (raw: string) =>
  raw.split("(")[0].replace(/[.,;:\s]+$/, "").trim();

// "Spanish (official)" → "Spanish". Deliberately strict: entries that list
// several languages, or bury the official one in prose, are skipped rather
// than guessed at, so a language question always has one defensible answer.
const parseOfficialLanguage = (raw: string): string | undefined => {
  const match = raw.match(/^([\p{Lu}][\p{L}'’-]*)\s*\((?:official|oficial)\)/u);
  return match?.[1];
};

// "Atlantic Ocean", "Bay of Bengal (part of the Indian Ocean)." → first body
// of water. Landlocked entries are filtered out by the caller.
const parseSea = (raw: string): string | undefined => {
  if (/landlocked|sin (?:salida|litoral)|no tiene costa/i.test(raw))
    return undefined;
  const first = raw.split(/[,;.]|\band\b|\by\b/)[0]?.trim();
  if (!first || first.length < 3 || first.length > 40) return undefined;
  return first;
};

// "Approximately 46 million inhabitants (2023)" /
// "Aproximadamente 46 millones de habitantes (2024)" → 46_000_000.
const parsePopulation = (raw: string): number | undefined => {
  const match = raw.match(
    /([\d]+(?:[.,]\d+)?)\s*(billion|mil millones|million|millones|thousand|mil)?/i
  );
  if (!match) return undefined;
  const value = Number(match[1].replace(",", "."));
  if (!Number.isFinite(value)) return undefined;
  const unit = (match[2] ?? "").toLowerCase();
  if (/billion|mil millones/.test(unit)) return value * 1_000_000_000;
  if (/million|millones/.test(unit)) return value * 1_000_000;
  if (/thousand|mil/.test(unit)) return value * 1_000;
  return value;
};

const formatPopulation = (value: number, isSpanish: boolean) => {
  if (value >= 1_000_000_000)
    return `${(value / 1_000_000_000).toFixed(1)} ${
      isSpanish ? "mil millones" : "billion"
    }`;
  if (value >= 1_000_000)
    return `${Math.round(value / 1_000_000)} ${
      isSpanish ? "millones" : "million"
    }`;
  return value.toLocaleString(isSpanish ? "es-AR" : "en-US");
};

interface CountryFacts {
  country: Country;
  code: string;
  displayName: string;
  capital?: string;
  currency?: string;
  continent?: Continent;
  language?: string;
  sea?: string;
  population?: number;
  populationText?: string;
  /** Display names of the countries this one shares a land border with. */
  borders: string[];
}

const buildFacts = (language: AvailableLanguages): CountryFacts[] => {
  const isSpanish = language === AvailableLanguages.ES;
  const dataset = (isSpanish ? SpanishData : EnglishData) as Record<
    string,
    Record<string, unknown>
  >;

  const countries = (countriesData as Country[]).filter(
    (country) => !country["not-a-country"]
  );

  const nameOf = (country: Country) =>
    isSpanish ? country["spanish-name"] : country["english-name"];

  const str = (info: Record<string, unknown> | undefined, ...keys: string[]) => {
    for (const key of keys) {
      const value = info?.[key];
      if (typeof value === "string" && value.trim()) return value.trim();
    }
    return undefined;
  };

  const facts = countries.map((country) => {
    const displayName = nameOf(country);
    const info = dataset[displayName];

    const rawCapital = str(info, "Capital");
    const rawCurrency = str(info, "Currency", "Moneda");
    // Continent always comes from the English dataset: which continent a
    // country sits on doesn't depend on the reading language, and the
    // Spanish strings ("América del Sur", "África") don't match the
    // English-worded patterns resolveContinent uses — reading them there
    // silently collapsed the Spanish pool down to Asia plus a dozen
    // hard-coded overrides.
    const rawContinent = (
      EnglishData as Record<string, { Continent?: string }>
    )[country["english-name"]]?.Continent;
    const rawLanguage = str(info, "Language", "Idioma");
    const rawSea = str(info, "Seas and Oceans", "Mares y Océanos");
    const rawPopulation = str(info, "Population", "Cantidad de Población");

    return {
      country,
      code: country.initials.toLowerCase(),
      displayName,
      capital: rawCapital ? cleanCapitalLabel(rawCapital) : undefined,
      currency: rawCurrency ? cleanCurrencyLabel(rawCurrency) : undefined,
      continent: resolveContinent(country["english-name"], rawContinent),
      language: rawLanguage ? parseOfficialLanguage(rawLanguage) : undefined,
      sea: rawSea ? parseSea(rawSea) : undefined,
      population: rawPopulation ? parsePopulation(rawPopulation) : undefined,
      populationText: rawPopulation,
      borders: [] as string[],
      _rawBorders: str(
        info,
        "Bordering Countries",
        "Neighboring Countries",
        "Países Limítrofes"
      ),
    };
  });

  // Border data is prose ("India to the west, north, and east; Myanmar to the
  // southeast."), so resolve it by scanning for names we actually know rather
  // than trying to split the sentence. Entries that say the country has no
  // land borders are skipped outright — several of those go on to mention
  // nearby countries ("Australia has no land borders, but is close to New
  // Zealand..."), which a naive scan would read as neighbours.
  const byNormalizedName = facts.map((fact) => ({
    fact,
    normalized: parsedCountryName(fact.displayName),
  }));

  facts.forEach((fact) => {
    const raw = fact._rawBorders;
    if (!raw) return;
    if (/no land borders|no tiene (?:fronteras|límites)|insular/i.test(raw))
      return;
    const normalizedRaw = parsedCountryName(raw);
    fact.borders = byNormalizedName
      .filter(
        ({ fact: other, normalized }) =>
          other !== fact &&
          normalized.length > 3 &&
          normalizedRaw.includes(normalized)
      )
      .map(({ fact: other }) => other.displayName);
  });

  return facts.map(({ _rawBorders, ...fact }) => fact);
};

// ---------------------------------------------------------------------------
// Difficulty tuning
// ---------------------------------------------------------------------------

// A deliberately small, globally-recognizable roster (ISO 3166-1 alpha-2
// codes) used to keep "easy" mode approachable: familiar targets, and wrong
// options that are also familiar instead of obscure lookalikes.
const WELL_KNOWN_CODES = new Set([
  "us", "ca", "mx", "br", "ar", "cl", "co", "pe", "ve", "cu",
  "gb", "fr", "de", "it", "es", "pt", "nl", "be", "se", "no",
  "ch", "gr", "pl", "ua", "ie", "ru",
  "cn", "jp", "kr", "in", "id", "th", "vn", "ph", "pk", "sa",
  "tr", "il", "ir", "iq", "ae",
  "eg", "za", "ng", "ke", "ma", "et",
  "au", "nz",
]);

// Difficulty is structural, not just a different country roster: each tier
// asks a different *kind* of question, with a different number of options.
//   easy   — recognition, with the flag on screen as support
//   medium — recall, no visual crutch, any country in the world
//   hard   — reasoning: combine two facts, rule one out, or compare
const TYPES_BY_DIFFICULTY: Record<AutotestDifficulty, AutotestQuestionType[]> = {
  easy: ["flag", "flagReverse", "continent", "capital"],
  medium: [
    "capital",
    "currency",
    "continent",
    "flag",
    "language",
    "sea",
    "border",
    "mapPoint",
  ],
  hard: [
    "notBorder",
    "oddOneOut",
    "combo",
    "biggestPopulation",
    "capital",
    "currency",
    "mapPoint",
  ],
};

const OPTION_COUNT: Record<AutotestDifficulty, number> = {
  easy: 3,
  medium: 4,
  hard: 5,
};

// Countries whose real capital is a widely-known "trap" — a bigger or more
// famous city that people commonly (and wrongly) assume is the capital.
const HARD_CAPITAL_TRAPS: Record<string, { en: string; es: string }> = {
  au: { en: "Sydney", es: "Sídney" },
  tr: { en: "Istanbul", es: "Estambul" },
  ca: { en: "Toronto", es: "Toronto" },
  ng: { en: "Lagos", es: "Lagos" },
  za: { en: "Cape Town", es: "Ciudad del Cabo" },
  ci: { en: "Abidjan", es: "Abiyán" },
  mm: { en: "Yangon", es: "Rangún" },
  lk: { en: "Colombo", es: "Colombo" },
  bo: { en: "La Paz", es: "La Paz" },
  br: { en: "Rio de Janeiro", es: "Río de Janeiro" },
  ch: { en: "Zurich", es: "Zúrich" },
  us: { en: "New York City", es: "Nueva York" },
  vn: { en: "Ho Chi Minh City", es: "Ciudad Ho Chi Minh" },
  ma: { en: "Casablanca", es: "Casablanca" },
  ec: { en: "Guayaquil", es: "Guayaquil" },
  pk: { en: "Karachi", es: "Karachi" },
  kz: { en: "Almaty", es: "Almaty" },
  in: { en: "Mumbai", es: "Bombay" },
  cn: { en: "Shanghai", es: "Shanghái" },
  bj: { en: "Cotonou", es: "Cotonú" },
};

// Countries that are commonly misplaced on a continent map — usually because
// they straddle two continents, or belong politically to a country on
// another continent entirely. The trap is the intuitive-but-wrong continent.
const HARD_CONTINENT_TRAPS: Record<string, Continent> = {
  ge: "Europe",
  am: "Europe",
  az: "Europe",
  cy: "Europe",
  kz: "Europe",
  tr: "Europe",
  ru: "Europe",
  eg: "Asia",
  gf: "Europe",
  gl: "Europe",
};

// Currencies that are also legal tender somewhere unexpected.
const HARD_CURRENCY_TRAPS: Record<string, { en: string; es: string }> = {
  pa: { en: "US Dollar (USD)", es: "Dólar estadounidense (USD)" },
};

// Flags that are genuinely easy to mix up (near-identical colors/layout).
const HARD_FLAG_CONFUSABLES: Array<[string, string]> = [
  ["td", "ro"],
  ["mc", "id"],
  ["ie", "ci"],
  ["ve", "co"],
  ["sk", "si"],
  ["au", "nz"],
  ["lu", "nl"],
];

const flagConfusablesFor = (code: string): string[] =>
  HARD_FLAG_CONFUSABLES.filter((pair) => pair.includes(code)).map((pair) =>
    pair[0] === code ? pair[1] : pair[0]
  );

const flagUrlFor = (code: string) => `https://flagcdn.com/w320/${code}.png`;

// ---------------------------------------------------------------------------
// Copy
// ---------------------------------------------------------------------------

const copy = {
  en: {
    capital: (c: string) => `What is the capital of ${c}?`,
    continent: (c: string) => `Which continent is ${c} in?`,
    currency: (c: string) => `What is the currency of ${c}?`,
    flag: "Which country does this flag belong to?",
    flagReverse: (c: string) => `Which of these is the flag of ${c}?`,
    language: (c: string) => `What is the official language of ${c}?`,
    sea: (c: string) => `Which body of water does ${c} border?`,
    border: (c: string) => `Which of these countries borders ${c}?`,
    notBorder: (c: string) => `Which of these does NOT border ${c}?`,
    oddOneOut: (k: string) => `Which of these is NOT in ${k}?`,
    combo: (k: string, l: string) =>
      `Which country is in ${k} and has ${l} as an official language?`,
    biggestPopulation: "Which of these countries has the largest population?",
    mapPoint: (c: string) => `Find ${c} on the map.`,
    exCapital: (c: string, v: string) => `${v} is the capital of ${c}.`,
    exCapitalTrap: (t: string) =>
      ` ${t} is the better-known city, but it isn't the capital.`,
    exContinent: (c: string, v: string) => `${c} is in ${v}.`,
    exContinentTrap: " It's often placed on the wrong continent.",
    exCurrency: (c: string, v: string) => `${c} uses the ${v}.`,
    exFlag: (c: string) => `That's the flag of ${c}.`,
    exFlagTrap: (o: string) => ` It's easily confused with ${o}'s.`,
    exLanguage: (c: string, v: string) =>
      `${v} is the official language of ${c}.`,
    exSea: (c: string, v: string) => `${c} has a coast on the ${v}.`,
    exBorder: (a: string, c: string, list: string) =>
      `${a} borders ${c}. Its neighbours are: ${list}.`,
    exNotBorder: (a: string, c: string, list: string) =>
      `${a} does not border ${c}. Its neighbours are: ${list}.`,
    exOddOneOut: (a: string, k: string) => `${a} is in ${k}.`,
    exCombo: (a: string, k: string, l: string) =>
      `${a} is in ${k} and its official language is ${l}.`,
    exBiggestPopulation: (a: string, v: string) =>
      `${a} has the largest population of the four: about ${v} people.`,
    exMapPoint: (c: string, k: string) => `${c} is in ${k}.`,
  },
  es: {
    capital: (c: string) => `¿Cuál es la capital de ${c}?`,
    continent: (c: string) => `¿En qué continente está ${c}?`,
    currency: (c: string) => `¿Cuál es la moneda de ${c}?`,
    flag: "¿De qué país es esta bandera?",
    flagReverse: (c: string) => `¿Cuál de estas es la bandera de ${c}?`,
    language: (c: string) => `¿Cuál es el idioma oficial de ${c}?`,
    sea: (c: string) => `¿Sobre qué mar u océano tiene costa ${c}?`,
    border: (c: string) => `¿Cuál de estos países limita con ${c}?`,
    notBorder: (c: string) => `¿Cuál de estos NO limita con ${c}?`,
    oddOneOut: (k: string) => `¿Cuál de estos NO está en ${k}?`,
    combo: (k: string, l: string) =>
      `¿Qué país está en ${k} y tiene el ${l} como idioma oficial?`,
    biggestPopulation: "¿Cuál de estos países tiene la mayor población?",
    mapPoint: (c: string) => `Encontrá ${c} en el mapa.`,
    exCapital: (c: string, v: string) => `${v} es la capital de ${c}.`,
    exCapitalTrap: (t: string) =>
      ` ${t} es la ciudad más conocida, pero no es la capital.`,
    exContinent: (c: string, v: string) => `${c} está en ${v}.`,
    exContinentTrap: " Suele ubicarse en el continente equivocado.",
    exCurrency: (c: string, v: string) => `${c} usa el ${v}.`,
    exFlag: (c: string) => `Es la bandera de ${c}.`,
    exFlagTrap: (o: string) => ` Se confunde fácil con la de ${o}.`,
    exLanguage: (c: string, v: string) => `El idioma oficial de ${c} es ${v}.`,
    exSea: (c: string, v: string) => `${c} tiene costa sobre ${v}.`,
    exBorder: (a: string, c: string, list: string) =>
      `${a} limita con ${c}. Sus países limítrofes son: ${list}.`,
    exNotBorder: (a: string, c: string, list: string) =>
      `${a} no limita con ${c}. Sus limítrofes son: ${list}.`,
    exOddOneOut: (a: string, k: string) => `${a} está en ${k}.`,
    exCombo: (a: string, k: string, l: string) =>
      `${a} está en ${k} y su idioma oficial es ${l}.`,
    exBiggestPopulation: (a: string, v: string) =>
      `${a} es el más poblado de los cuatro: alrededor de ${v} de personas.`,
    exMapPoint: (c: string, k: string) => `${c} está en ${k}.`,
  },
};

const plain = (labels: string[]): AutotestOption[] =>
  labels.map((label) => ({ label }));

// Source data isn't consistently capitalized ("US dollar" vs "US Dollar"), so
// two options that are really the same answer can otherwise slip past an
// exact-string check and show up side by side.
const normalize = (value: string) => value.trim().toLowerCase();

const buildChoice = (
  correct: string,
  wrong: string[],
  rng: Rng
): { options: AutotestOption[]; correctIndex: number } => {
  const labels = shuffle([correct, ...wrong], rng);
  return { options: plain(labels), correctIndex: labels.indexOf(correct) };
};

/**
 * Picks the subject country for a question, biasing the pool toward the
 * requested difficulty: familiar countries for "easy", deliberate traps or
 * obscure ones for "hard", anything for "medium".
 */
const pickTarget = (
  candidates: CountryFacts[],
  difficulty: AutotestDifficulty,
  rng: Rng,
  hasTrap: (f: CountryFacts) => boolean = () => false
): CountryFacts | undefined => {
  if (candidates.length === 0) return undefined;

  if (difficulty === "easy") {
    const wellKnown = candidates.filter((f) => WELL_KNOWN_CODES.has(f.code));
    return pickOne(wellKnown.length > 0 ? wellKnown : candidates, rng);
  }

  if (difficulty === "hard") {
    const trapped = candidates.filter(hasTrap);
    if (trapped.length > 0 && rng() < 0.6) return pickOne(trapped, rng);
    const obscure = candidates.filter((f) => !WELL_KNOWN_CODES.has(f.code));
    return pickOne(obscure.length > 0 ? obscure : candidates, rng);
  }

  return pickOne(candidates, rng);
};

const eligiblePool = (
  facts: CountryFacts[],
  difficulty: AutotestDifficulty
): CountryFacts[] =>
  difficulty === "easy"
    ? facts.filter((f) => WELL_KNOWN_CODES.has(f.code))
    : facts;

// ---------------------------------------------------------------------------
// Question builders
// ---------------------------------------------------------------------------

interface BuildContext {
  facts: CountryFacts[];
  isSpanish: boolean;
  difficulty: AutotestDifficulty;
  rng: Rng;
  t: typeof copy.en;
  lang: AvailableLanguages;
}

type Builder = (ctx: BuildContext) => AutotestQuestion | undefined;

/** Collects `count` distinct wrong values, ignoring case-only duplicates. */
const collectWrong = (
  pool: CountryFacts[],
  valueOf: (f: CountryFacts) => string | undefined,
  exclude: string[],
  count: number,
  rng: Rng
): string[] => {
  const seen = new Set(exclude.map(normalize));
  const out: string[] = [];
  for (const candidate of shuffle(pool, rng)) {
    if (out.length >= count) break;
    const value = valueOf(candidate);
    if (!value) continue;
    const key = normalize(value);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(value);
  }
  return out;
};

const buildValueQuestion = (
  ctx: BuildContext,
  type: "capital" | "currency",
  field: "capital" | "currency"
): AutotestQuestion | undefined => {
  const { facts, difficulty, rng, t, isSpanish } = ctx;
  const traps = type === "capital" ? HARD_CAPITAL_TRAPS : HARD_CURRENCY_TRAPS;
  const pool = eligiblePool(facts, difficulty).filter((f) => f[field]);
  if (pool.length < 4) return undefined;

  const target = pickTarget(pool, difficulty, rng, (f) => !!traps[f.code]);
  if (!target) return undefined;

  const correct = target[field] as string;
  const trap = difficulty === "hard" ? traps[target.code] : undefined;
  const trapValue = trap ? (isSpanish ? trap.es : trap.en) : undefined;

  const needed = OPTION_COUNT[difficulty] - 1 - (trapValue ? 1 : 0);
  const wrong = collectWrong(
    pool.filter((f) => f !== target),
    (f) => f[field],
    [correct, ...(trapValue ? [trapValue] : [])],
    needed,
    rng
  );
  if (trapValue) wrong.push(trapValue);
  if (wrong.length === 0) return undefined;

  const { options, correctIndex } = buildChoice(correct, wrong, rng);
  const explanation =
    type === "capital"
      ? t.exCapital(target.displayName, correct) +
        (trapValue ? t.exCapitalTrap(trapValue) : "")
      : t.exCurrency(target.displayName, correct);

  return {
    type,
    countryName: target.displayName,
    prompt: t[type](target.displayName),
    options,
    correctIndex,
    explanation,
    learnMoreCountry: target.displayName,
  };
};

const buildContinent: Builder = (ctx) => {
  const { facts, difficulty, rng, t, lang } = ctx;
  const pool = eligiblePool(facts, difficulty).filter((f) => f.continent);
  const target = pickTarget(
    pool,
    difficulty,
    rng,
    (f) => !!HARD_CONTINENT_TRAPS[f.code]
  );
  if (!target?.continent) return undefined;

  const labels = CONTINENT_LABELS[lang];
  const correct = labels[target.continent];
  const trapContinent =
    difficulty === "hard" ? HARD_CONTINENT_TRAPS[target.code] : undefined;

  const others = CONTINENTS.filter(
    (c) => c !== target.continent && c !== trapContinent
  );
  const wrong = sample(
    others,
    OPTION_COUNT[difficulty] - 1 - (trapContinent ? 1 : 0),
    rng
  ).map((c) => labels[c]);
  if (trapContinent) wrong.push(labels[trapContinent]);

  const { options, correctIndex } = buildChoice(correct, wrong, rng);
  return {
    type: "continent",
    countryName: target.displayName,
    prompt: t.continent(target.displayName),
    options,
    correctIndex,
    explanation:
      t.exContinent(target.displayName, correct) +
      (trapContinent ? t.exContinentTrap : ""),
    learnMoreCountry: target.displayName,
  };
};

const buildFlag: Builder = (ctx) => {
  const { facts, difficulty, rng, t } = ctx;
  const pool = eligiblePool(facts, difficulty).filter((f) => f.code);
  if (pool.length < 4) return undefined;

  const target = pickTarget(
    pool,
    difficulty,
    rng,
    (f) => flagConfusablesFor(f.code).length > 0
  );
  if (!target) return undefined;

  const byCode = new Map(facts.map((f) => [f.code, f]));
  const confusables =
    difficulty === "hard"
      ? flagConfusablesFor(target.code)
          .map((code) => byCode.get(code)?.displayName)
          .filter((name): name is string => !!name)
      : [];

  const wrong = [
    ...confusables,
    ...collectWrong(
      pool.filter((f) => f !== target),
      (f) => f.displayName,
      [target.displayName, ...confusables],
      OPTION_COUNT[difficulty] - 1 - confusables.length,
      rng
    ),
  ].slice(0, OPTION_COUNT[difficulty] - 1);

  const { options, correctIndex } = buildChoice(target.displayName, wrong, rng);
  return {
    type: "flag",
    countryName: target.displayName,
    prompt: t.flag,
    flagUrl: flagUrlFor(target.code),
    options,
    correctIndex,
    explanation:
      t.exFlag(target.displayName) +
      (confusables.length > 0 ? t.exFlagTrap(confusables[0]) : ""),
    learnMoreCountry: target.displayName,
  };
};

// Country → flag. The mirror image of `buildFlag`, and the most forgiving
// format there is, which is why it's an "easy"-tier question.
const buildFlagReverse: Builder = (ctx) => {
  const { facts, difficulty, rng, t } = ctx;
  const pool = eligiblePool(facts, difficulty).filter((f) => f.code);
  if (pool.length < 4) return undefined;

  const target = pickTarget(pool, difficulty, rng);
  if (!target) return undefined;

  const wrong = sample(
    pool.filter((f) => f.code !== target.code),
    OPTION_COUNT[difficulty] - 1,
    rng
  );
  if (wrong.length < 2) return undefined;

  const chosen = shuffle([target, ...wrong], rng);
  return {
    type: "flagReverse",
    countryName: target.displayName,
    prompt: t.flagReverse(target.displayName),
    options: chosen.map((f) => ({
      label: f.displayName,
      flagUrl: flagUrlFor(f.code),
    })),
    correctIndex: chosen.indexOf(target),
    explanation: t.exFlag(target.displayName),
    learnMoreCountry: target.displayName,
  };
};

const buildLanguage: Builder = (ctx) => {
  const { facts, difficulty, rng, t } = ctx;
  const pool = eligiblePool(facts, difficulty).filter((f) => f.language);
  if (pool.length < 4) return undefined;

  const target = pickTarget(pool, difficulty, rng);
  if (!target?.language) return undefined;

  const wrong = collectWrong(
    pool.filter((f) => f !== target),
    (f) => f.language,
    [target.language],
    OPTION_COUNT[difficulty] - 1,
    rng
  );
  if (wrong.length < 2) return undefined;

  const { options, correctIndex } = buildChoice(target.language, wrong, rng);
  return {
    type: "language",
    countryName: target.displayName,
    prompt: t.language(target.displayName),
    options,
    correctIndex,
    explanation: t.exLanguage(target.displayName, target.language),
    learnMoreCountry: target.displayName,
  };
};

const buildSea: Builder = (ctx) => {
  const { facts, difficulty, rng, t } = ctx;
  const pool = eligiblePool(facts, difficulty).filter((f) => f.sea);
  if (pool.length < 4) return undefined;

  const target = pickTarget(pool, difficulty, rng);
  if (!target?.sea) return undefined;

  const wrong = collectWrong(
    pool.filter((f) => f !== target),
    (f) => f.sea,
    [target.sea],
    OPTION_COUNT[difficulty] - 1,
    rng
  );
  if (wrong.length < 2) return undefined;

  const { options, correctIndex } = buildChoice(target.sea, wrong, rng);
  return {
    type: "sea",
    countryName: target.displayName,
    prompt: t.sea(target.displayName),
    options,
    correctIndex,
    explanation: t.exSea(target.displayName, target.sea),
    learnMoreCountry: target.displayName,
  };
};

// "Which of these borders X?" — the wrong options are countries that really
// don't, drawn from the same pool, so the question stays fair.
const buildBorder =
  (negate: boolean): Builder =>
  (ctx) => {
    const { facts, difficulty, rng, t } = ctx;
    const pool = eligiblePool(facts, difficulty).filter(
      (f) => f.borders.length >= (negate ? 3 : 1)
    );
    const target = pickTarget(pool, difficulty, rng);
    if (!target) return undefined;

    const borderSet = new Set(target.borders);
    const nonNeighbours = facts
      .filter(
        (f) => f !== target && !borderSet.has(f.displayName) && f.displayName
      )
      .map((f) => f.displayName);

    const optionCount = OPTION_COUNT[difficulty];
    const neighbourNames = target.borders;

    if (negate) {
      // One non-neighbour hidden among real neighbours.
      if (neighbourNames.length < optionCount - 1 || nonNeighbours.length === 0)
        return undefined;
      const correct = pickOne(nonNeighbours, rng);
      if (!correct) return undefined;
      const wrong = sample(neighbourNames, optionCount - 1, rng);
      const { options, correctIndex } = buildChoice(correct, wrong, rng);
      return {
        type: "notBorder",
        countryName: target.displayName,
        prompt: t.notBorder(target.displayName),
        options,
        correctIndex,
        explanation: t.exNotBorder(
          correct,
          target.displayName,
          neighbourNames.join(", ")
        ),
        learnMoreCountry: target.displayName,
      };
    }

    const correct = pickOne(neighbourNames, rng);
    if (!correct) return undefined;
    const wrong = sample(nonNeighbours, optionCount - 1, rng);
    if (wrong.length < 2) return undefined;
    const { options, correctIndex } = buildChoice(correct, wrong, rng);
    return {
      type: "border",
      countryName: target.displayName,
      prompt: t.border(target.displayName),
      options,
      correctIndex,
      explanation: t.exBorder(
        correct,
        target.displayName,
        neighbourNames.join(", ")
      ),
      learnMoreCountry: target.displayName,
    };
  };

const buildOddOneOut: Builder = (ctx) => {
  const { facts, rng, t, lang, difficulty } = ctx;
  const byContinent = new Map<Continent, CountryFacts[]>();
  facts.forEach((f) => {
    if (!f.continent) return;
    const list = byContinent.get(f.continent);
    if (list) list.push(f);
    else byContinent.set(f.continent, [f]);
  });

  const optionCount = OPTION_COUNT[difficulty];
  const usable = CONTINENTS.filter(
    (c) => (byContinent.get(c)?.length ?? 0) >= optionCount
  );
  const theme = pickOne(usable, rng);
  if (!theme) return undefined;

  const inside = sample(byContinent.get(theme) ?? [], optionCount - 1, rng);
  const outsidePool = facts.filter((f) => f.continent && f.continent !== theme);
  const outsider = pickOne(outsidePool, rng);
  if (!outsider?.continent || inside.length < optionCount - 1) return undefined;

  const labels = CONTINENT_LABELS[lang];
  const { options, correctIndex } = buildChoice(
    outsider.displayName,
    inside.map((f) => f.displayName),
    rng
  );
  return {
    type: "oddOneOut",
    countryName: outsider.displayName,
    prompt: t.oddOneOut(labels[theme]),
    options,
    correctIndex,
    explanation: t.exOddOneOut(
      outsider.displayName,
      labels[outsider.continent]
    ),
    learnMoreCountry: outsider.displayName,
  };
};

// Two facts at once: continent *and* official language. Every distractor
// satisfies exactly one of the two conditions, so guessing on either alone
// isn't enough.
const buildCombo: Builder = (ctx) => {
  const { facts, rng, t, lang, difficulty } = ctx;
  const withBoth = facts.filter((f) => f.continent && f.language);
  const target = pickOne(withBoth, rng);
  if (!target?.continent || !target.language) return undefined;

  const sameContinentOtherLanguage = withBoth.filter(
    (f) => f.continent === target.continent && f.language !== target.language
  );
  const sameLanguageOtherContinent = withBoth.filter(
    (f) => f.language === target.language && f.continent !== target.continent
  );
  if (
    sameContinentOtherLanguage.length === 0 ||
    sameLanguageOtherContinent.length === 0
  )
    return undefined;

  // Half the distractors share the continent, half share the language, so
  // neither clue alone is enough. If one pool runs short, top up from the
  // other rather than emitting a question with too few options.
  const needed = OPTION_COUNT[difficulty] - 1;
  const half = Math.max(1, Math.floor(needed / 2));
  const fromContinent = sample(sameContinentOtherLanguage, half, rng);
  const fromLanguage = sample(
    sameLanguageOtherContinent,
    needed - fromContinent.length,
    rng
  );
  const picked = [...fromContinent, ...fromLanguage];
  if (picked.length < needed) {
    const already = new Set(picked);
    const filler = [
      ...sameContinentOtherLanguage,
      ...sameLanguageOtherContinent,
    ].filter((f) => !already.has(f) && f !== target);
    picked.push(...sample(filler, needed - picked.length, rng));
  }

  const wrong = picked.map((f) => f.displayName);
  if (wrong.length < 2) return undefined;

  const labels = CONTINENT_LABELS[lang];
  const { options, correctIndex } = buildChoice(
    target.displayName,
    wrong,
    rng
  );
  return {
    type: "combo",
    countryName: target.displayName,
    prompt: t.combo(labels[target.continent], target.language),
    options,
    correctIndex,
    explanation: t.exCombo(
      target.displayName,
      labels[target.continent],
      target.language
    ),
    learnMoreCountry: target.displayName,
  };
};

const buildBiggestPopulation: Builder = (ctx) => {
  const { facts, rng, t, isSpanish, difficulty } = ctx;
  const pool = facts.filter((f) => typeof f.population === "number");
  if (pool.length < 6) return undefined;

  // The winner is one of the options, so sample a full set (not set-minus-one).
  const chosen = sample(pool, OPTION_COUNT[difficulty], rng);
  if (chosen.length < 3) return undefined;

  const winner = [...chosen].sort(
    (a, b) => (b.population ?? 0) - (a.population ?? 0)
  )[0];
  // Reject near-ties: "which is bigger" between two countries a few hundred
  // thousand apart is trivia, not knowledge.
  const runnerUp = [...chosen]
    .filter((f) => f !== winner)
    .sort((a, b) => (b.population ?? 0) - (a.population ?? 0))[0];
  if (
    !winner.population ||
    !runnerUp?.population ||
    winner.population < runnerUp.population * 1.35
  )
    return undefined;

  const { options, correctIndex } = buildChoice(
    winner.displayName,
    chosen.filter((f) => f !== winner).map((f) => f.displayName),
    rng
  );
  return {
    type: "biggestPopulation",
    countryName: winner.displayName,
    prompt: t.biggestPopulation,
    options,
    correctIndex,
    explanation: t.exBiggestPopulation(
      winner.displayName,
      formatPopulation(winner.population, isSpanish)
    ),
    learnMoreCountry: winner.displayName,
  };
};

// Uses the app's own map as the answer surface: the options are rendered as
// a map the player taps, not as a list.
const buildMapPoint: Builder = (ctx) => {
  const { facts, difficulty, rng, t, lang } = ctx;
  const pool = eligiblePool(facts, difficulty).filter(
    (f) => f.continent && f.code
  );
  const target = pickTarget(pool, difficulty, rng);
  if (!target?.continent) return undefined;

  const labels = CONTINENT_LABELS[lang];
  return {
    type: "mapPoint",
    countryName: target.displayName,
    prompt: t.mapPoint(target.displayName),
    mapTargetName: target.country["english-name"],
    mapTargetContinent: target.continent,
    options: [],
    correctIndex: 0,
    explanation: t.exMapPoint(target.displayName, labels[target.continent]),
    learnMoreCountry: target.displayName,
  };
};

const BUILDERS: Record<AutotestQuestionType, Builder> = {
  capital: (ctx) => buildValueQuestion(ctx, "capital", "capital"),
  currency: (ctx) => buildValueQuestion(ctx, "currency", "currency"),
  continent: buildContinent,
  flag: buildFlag,
  flagReverse: buildFlagReverse,
  language: buildLanguage,
  sea: buildSea,
  border: buildBorder(false),
  notBorder: buildBorder(true),
  oddOneOut: buildOddOneOut,
  combo: buildCombo,
  biggestPopulation: buildBiggestPopulation,
  mapPoint: buildMapPoint,
};

export const buildAutotestQuestions = (
  count: number,
  language: AvailableLanguages,
  difficulty: AutotestDifficulty = "medium",
  options: { rng?: Rng; types?: AutotestQuestionType[] } = {}
): AutotestQuestion[] => {
  const rng = options.rng ?? Math.random;
  const isSpanish = language === AvailableLanguages.ES;
  const facts = buildFacts(language);
  const ctx: BuildContext = {
    facts,
    isSpanish,
    difficulty,
    rng,
    t: isSpanish ? copy.es : copy.en,
    lang: language,
  };

  const availableTypes = options.types?.length
    ? options.types
    : TYPES_BY_DIFFICULTY[difficulty];

  const questions: AutotestQuestion[] = [];
  const usedCountries = new Set<string>();
  const usedPrompts = new Set<string>();
  let attempts = 0;
  const maxAttempts = count * 40;

  // Rotate through the tier's types so a run samples all of them instead of
  // landing on "capital" nine times out of ten.
  let queue: AutotestQuestionType[] = [];

  while (questions.length < count && attempts < maxAttempts) {
    attempts += 1;
    if (queue.length === 0) queue = shuffle(availableTypes, rng);
    const type = queue.pop() as AutotestQuestionType;

    const question = BUILDERS[type](ctx);
    if (!question) continue;
    if (usedCountries.has(question.countryName)) continue;
    if (usedPrompts.has(question.prompt)) continue;

    usedCountries.add(question.countryName);
    usedPrompts.add(question.prompt);
    questions.push(question);
  }

  return questions;
};
